"use client";
import { useState } from "react";
import { MapPin } from "lucide-react";
import { useAppContext } from "@/context/AppContext";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

export function SiteFooter() {
  const { theme } = useAppContext();
  const [showTerms, setShowTerms] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) element.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <footer className="footer bg-[#1e1e2f] text-white py-10 px-5 font-sans">
     <div className="footer-container flex flex-col md:flex-row justify-between max-w-6xl mx-auto gap-8">
  	{/* Left Section */}
  	  <div className="footer-left flex-1">
  	    <h2 className="footer-title text-2xl font-semibold text-white mb-3 flex items-center gap-2">
      	      <MapPin className="h-6 w-6 text-emerald-500" />
      		CheckIn-Out-Pro
    	    </h2>

	    <br/>
    	    <p className="footer-description text-base text-gray-300 leading-relaxed mb-4">
      	    Empowering businesses with advanced <br /> GPS tracking and
     	    workforce management solutions.
    	    </p>
	   <br/>
    	   <div className="footer-rights flex flex-wrap items-center gap-3 text-sm text-gray-500">
      	   <span>© {new Date().getFullYear()} CheckIn-Out-Pro. All rights reserved | Developed by </span>
      
      	   <div className="flex items-center gap-2 border-l border-gray-800 pl-3">
             <a 
               href="https://www.algroton.com/" 
               target="_blank" 
               rel="noopener noreferrer"
               className="transition-all hover:opacity-80 active:scale-95"
               >
            <img 
              src="/logo.png" 
              alt="Algroton" 
              className="h-5 w-auto object-contain brightness-90 hover:brightness-110" 
            />
           </a>
      	  </div>
    	</div>
       </div>
   

	

        {/* Right Section */}
        <div className="footer-right">
          <h3 className="footer-links-title text-lg mb-3 text-white">
            Quick Links
          </h3>
          <ul className="footer-links list-none p-0">
            <li className="mb-2">
              <button
                onClick={() => scrollToSection("pricing")}
                className="text-white font-medium hover:text-emerald-400 transition-colors"
              >
                Pricing
              </button>
            </li>
            <li className="mb-2">
              <button
                onClick={() => scrollToSection("how-it-works")}
                className="text-white font-medium hover:text-emerald-400 transition-colors"
              >
                How It Works
              </button>
            </li>
            <li className="mb-2">
              <button
                onClick={() => scrollToSection("contact")}
                className="text-white font-medium hover:text-emerald-400 transition-colors"
              >
                Contact Us
              </button>
            </li>

            {/* Popup Links */}
            <li className="mb-2">
              <button
                onClick={() => setShowTerms(true)}
                className="text-white font-medium hover:text-emerald-400 transition-colors"
              >
                Terms & Conditions
              </button>
            </li>
            <li>
              <button
                onClick={() => setShowPrivacy(true)}
                className="text-white font-medium hover:text-emerald-400 transition-colors"
              >
                Privacy Policy
              </button>
            </li>
          </ul>
        </div>
      </div>

      {/* Terms & Conditions Modal */}
      <Dialog open={showTerms} onOpenChange={setShowTerms}>
        <DialogContent className="backdrop-blur-xl bg-white/10 dark:bg-gray-900/70 border border-white/20 text-white max-w-3xl max-h-[80vh] overflow-y-auto rounded-2xl shadow-lg">
          <DialogHeader>
            <DialogTitle className="text-2xl font-semibold mb-2">
              Terms & Conditions
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 text-gray-200 text-sm leading-relaxed">
            <p>
              Welcome to <strong>CheckIn-Out-Pro</strong>. These Terms govern
              your access and use of our workforce management and GPS tracking
              services. By continuing to use CheckIn-Out-Pro, you acknowledge
              and agree to these Terms.
            </p>
            <p>
              You agree to use our services responsibly, complying with
              applicable laws. You shall not engage in unauthorized access,
              tampering, or misuse of data or system functionality.
            </p>
            <p>
              CheckIn-Out-Pro may collect biometric (Face ID/Fingerprint) and
              geolocation data to ensure secure user authentication and accurate
              attendance tracking. Continued use constitutes your consent to
              such collection and use as outlined in our{" "}
              <button
                onClick={() => {
                  setShowTerms(false);
                  setShowPrivacy(true);
                }}
                className="text-emerald-400 underline hover:text-emerald-300"
              >
                Privacy Policy
              </button>
              .
            </p>
            <p>
              We may modify these Terms occasionally to reflect updates in
              service or regulation. Updated versions will be posted and take
              effect upon publication.
            </p>
          </div>
        </DialogContent>
      </Dialog>

      {/* Privacy Policy Modal */}
      <Dialog open={showPrivacy} onOpenChange={setShowPrivacy}>
        <DialogContent className="backdrop-blur-xl bg-white/10 dark:bg-gray-900/70 border border-white/20 text-white max-w-3xl max-h-[80vh] overflow-y-auto rounded-2xl shadow-lg">
          <DialogHeader>
            <DialogTitle className="text-2xl font-semibold mb-2">
              Privacy Policy
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 text-gray-200 text-sm leading-relaxed">
            <p>
              <strong>CheckIn-Out-Pro</strong> values your privacy and is
              committed to protecting your personal information. This policy
              explains how we collect, use, and safeguard your data.
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                We collect limited personal, biometric, and geolocation data to
                provide secure access and improve attendance accuracy.
              </li>
              <li>
                Cookies and analytics tools help us enhance performance and user
                experience. You can manage cookie preferences anytime.
              </li>
              <li>
                Your information will never be sold or shared without explicit
                consent, except when legally required.
              </li>
            </ul>
            <p>
              You can request account deletion, withdraw biometric consent, or
              opt out of tracking at any time by contacting our support team at{" "}
              <a
                href="mailto:support@checkinoutpro.com"
                className="text-emerald-400 underline hover:text-emerald-300"
              >
                support@checkinoutpro.com
              </a>
              .
            </p>
            <p>
              We continually improve our data protection standards and will
              update this policy as regulations evolve. Your continued use of
              CheckIn-Out-Pro signifies acceptance of these updates.
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </footer>
  );
}
